// styles/theme.js
// Premium Corporate Dark Theme - Professional & Modern

import { createTheme, alpha } from '@mui/material/styles';

// Paleta de colores corporativa
const corporateColors = {
  // Azul corporativo premium
  primary: {
    main: '#3B82F6',      // Azul vibrante pero profesional
    light: '#60A5FA',
    dark: '#2563EB',
    contrastText: '#FFFFFF',
  },
  // Verde esmeralda para éxito
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    contrastText: '#FFFFFF',
  },
  // Ámbar para advertencias
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    contrastText: '#000000',
  },
  // Rojo coral para errores
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    contrastText: '#FFFFFF',
  },
  // Cyan para información
  info: {
    main: '#06B6D4',
    light: '#22D3EE',
    dark: '#0891B2',
    contrastText: '#FFFFFF',
  },
};

// Grises refinados para dark mode
const neutralColors = {
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
  950: '#020617',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: corporateColors.primary,
    secondary: {
      main: '#8B5CF6',      // Púrpura elegante
      light: '#A78BFA',
      dark: '#7C3AED',
    },
    success: corporateColors.success,
    warning: corporateColors.warning,
    error: corporateColors.error,
    info: corporateColors.info,
    background: {
      default: neutralColors[950],   // Fondo muy oscuro
      paper: neutralColors[900],     // Tarjetas ligeramente más claras
    },
    text: {
      primary: neutralColors[50],
      secondary: neutralColors[400],
      disabled: neutralColors[600],
    },
    divider: alpha(neutralColors[400], 0.12),
    action: {
      hover: alpha(corporateColors.primary.main, 0.08),
      selected: alpha(corporateColors.primary.main, 0.16),
      disabled: neutralColors[600],
      disabledBackground: alpha(neutralColors[600], 0.12),
    },
  },

  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Arial", sans-serif',

    h1: {
      fontSize: '2.5rem',
      fontWeight: 800,
      letterSpacing: '-0.025em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.25,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 700,
      letterSpacing: '-0.015em',
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      letterSpacing: '-0.005em',
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      color: neutralColors[400],
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
  },

  shape: {
    borderRadius: 12,
  },

  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: `${neutralColors[700]} transparent`,
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: neutralColors[700],
            borderRadius: '4px',
            '&:hover': {
              background: neutralColors[600],
            },
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontWeight: 600,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 20px 0 rgba(59, 130, 246, 0.35)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: `0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 1px 2px -1px rgba(0, 0, 0, 0.1), inset 0 0 0 1px ${alpha(neutralColors[700], 0.5)}`,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${alpha(neutralColors[700], 0.5)}`,
          backgroundImage: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: alpha(corporateColors.primary.main, 0.3),
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: corporateColors.primary.main,
              },
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(corporateColors.primary.main, 0.15)}`,
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
        filled: {
          background: alpha(corporateColors.primary.main, 0.15),
          color: corporateColors.primary.light,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutralColors[800],
          borderRadius: 8,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '8px 12px',
          border: `1px solid ${neutralColors[700]}`,
        },
        arrow: {
          color: neutralColors[800],
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: alpha(neutralColors[950], 0.8),
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${alpha(neutralColors[700], 0.5)}`,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: neutralColors[900],
          borderRight: `1px solid ${alpha(neutralColors[700], 0.5)}`,
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '2px 0',
          transition: 'all 0.15s ease-in-out',
          '&.Mui-selected': {
            backgroundColor: alpha(corporateColors.primary.main, 0.15),
            '&:hover': {
              backgroundColor: alpha(corporateColors.primary.main, 0.2),
            },
          },
          '&:hover': {
            backgroundColor: alpha(neutralColors[700], 0.5),
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          border: `1px solid ${alpha(neutralColors[700], 0.5)}`,
          backgroundImage: 'none',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500,
        },
        standardSuccess: {
          backgroundColor: alpha(corporateColors.success.main, 0.15),
          color: corporateColors.success.light,
        },
        standardError: {
          backgroundColor: alpha(corporateColors.error.main, 0.15),
          color: corporateColors.error.light,
        },
        standardWarning: {
          backgroundColor: alpha(corporateColors.warning.main, 0.15),
          color: corporateColors.warning.light,
        },
        standardInfo: {
          backgroundColor: alpha(corporateColors.info.main, 0.15),
          color: corporateColors.info.light,
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: alpha(neutralColors[700], 0.5),
        },
        head: {
          fontWeight: 700,
          color: neutralColors[300],
          backgroundColor: alpha(neutralColors[800], 0.5),
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(neutralColors[800], 0.5),
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: 'none',
          minHeight: 48,
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${alpha(neutralColors[700], 0.5)}`,
          marginTop: 4,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 6px',
          '&.Mui-selected': {
            backgroundColor: alpha(corporateColors.primary.main, 0.15),
          },
        },
      },
    },
  },
});

export default theme;